/* eslint-disable @typescript-eslint/no-inferrable-types */
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, interval, Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { VAR_ACCESS_TOKEN, VAR_PUBLIC_ENDPOINTS, VAR_REFRESH_TOKEN } from '../../../../../shared/config/consts';
import { environment } from '../../../environments/environment';

export interface RequestOptions {
  headers?: HttpHeaders;
  params?: HttpParams;
  responseType?: 'json' | 'blob' | 'text' | 'arraybuffer';
}

interface RefreshTokenResponse {
  message: string;
  payload: {
    accessToken: string;
    refreshToken: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private refreshWatcherSub?: Subscription;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.startTokenRefreshWatcher();
  }

  /**
   * Check if a JWT token is expired
   */
   isTokenExpired(token: string): boolean {
    if (!token) return true;

    const expiryTime = this.getTokenExpiryTime(token);
    if (!expiryTime) {
      return true;
    }

    return Date.now() >= expiryTime;
  }

  /**
   * Get token expiry time in milliseconds
   */
  private getTokenExpiryTime(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload?.exp) {
        return null;
      }
      return payload.exp * 1000;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  /**
   * Check if token is about to expire within buffer
   */
  private isTokenExpiringSoon(token: string, bufferMs: number = 60000): boolean {
    const expiryTime = this.getTokenExpiryTime(token);
    if (!expiryTime) {
      return true;
    }
    return Date.now() >= (expiryTime - bufferMs);
  }

  /**
   * Background watcher to refresh token even without requests
   */
  private startTokenRefreshWatcher(): void {
    if (this.refreshWatcherSub) {
      return;
    }

    this.refreshWatcherSub = interval(30000).subscribe(() => {
      const accessToken = localStorage.getItem(VAR_ACCESS_TOKEN);
      if (!accessToken) {
        return;
      }

      if (this.isRefreshing || !this.isTokenExpiringSoon(accessToken)) {
        return;
      }

      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      this.refreshAccessToken()
        .pipe(
          finalize(() => {
            this.isRefreshing = false;
          })
        )
        .subscribe({
          next: (newToken) => {
            this.refreshTokenSubject.next(newToken);
          },
          error: () => {
            this.refreshTokenSubject.next(null);
          },
        });
    });
  }

  /**
   * Refresh the access token using refresh token
   */
  private refreshAccessToken(): Observable<string> {
    const refreshToken = localStorage.getItem(VAR_REFRESH_TOKEN);

    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      this.logout();
      return throwError(() => new Error('Session expired. Please login again.'));
    }

    return this.http.post<RefreshTokenResponse>(
      `${this.apiUrl}/auth/refresh`,
      { refreshToken }
    ).pipe(
      switchMap((response) => {
        const newAccessToken = response?.payload?.accessToken;
        const newRefreshToken = response?.payload?.refreshToken;

        if(!newAccessToken || !newRefreshToken){
          this.logout();
          return throwError(() => new Error('Failed to refresh token. Please login again.'));
        }
        
        localStorage.setItem(VAR_ACCESS_TOKEN, newAccessToken);
        localStorage.setItem(VAR_REFRESH_TOKEN, newRefreshToken);
        
        return new Observable<string>((observer) => {
          observer.next(newAccessToken);
          observer.complete();
        });
      }),
      catchError((error) => {
        console.log("REFRESH TOKEN ERROR: ", error)
        this.logout();
        return throwError(() => new Error('Failed to refresh token. Please login again.'));
      })
    );
  }

  /**
   * Get authorization headers with Bearer token (with auto-refresh)
   */
  private getAuthHeaders(endpoint:string): Observable<HttpHeaders>  {
    const accessToken = localStorage.getItem(VAR_ACCESS_TOKEN);
    const isNotPublicEndpoint = VAR_PUBLIC_ENDPOINTS.every(authEndpoint => !endpoint.includes(authEndpoint));

    if(isNotPublicEndpoint){
      if(!accessToken || this.isTokenExpired(accessToken)){
          if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
              filter(token => token !== null),
              take(1),
              switchMap(token => {
                return new Observable<HttpHeaders>((observer) => {
                  observer.next(new HttpHeaders().set('Authorization', `Bearer ${token}`));
                  observer.complete();
                });
              })
            );
          }

        // Start refreshing
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);          

        return this.refreshAccessToken().pipe(
          switchMap((newToken) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(newToken);
            return new Observable<HttpHeaders>((observer) => {
              observer.next(new HttpHeaders().set('Authorization', `Bearer ${newToken}`));
              observer.complete();
            });
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(null);
            return throwError(() => error);
          })
        );
      }
      
      return new Observable<HttpHeaders>((observer) => {
        observer.next(new HttpHeaders().set('Authorization', `Bearer ${accessToken}`));
        observer.complete();
      });
    }

    // Let the request continue without authorization header
    return new Observable<HttpHeaders>((observer) => {
      observer.next(new HttpHeaders());
      observer.complete();
    });
  }

  /**
   * Merge custom headers with auth headers
   */
  private mergeHeaders(customHeaders?: HttpHeaders, endpoint: string = ''): Observable<HttpHeaders> {
    return this.getAuthHeaders(endpoint).pipe(
      switchMap((authHeaders) => {
        const language = localStorage.getItem('translocoLang') || 'rw';
        let merged = authHeaders.set('accept-language', language);

        if (customHeaders) {
          customHeaders.keys().forEach(key => {
            const value = customHeaders.get(key);
            if (value) {
              merged = merged.set(key, value);
            }
          });
        }

        return new Observable<HttpHeaders>((observer) => {
          observer.next(merged);
          observer.complete();
        });
      })
    );
  }

  /**
   * Build full endpoint URL
   */
  private getUrl(endpoint: string): string {
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.apiUrl}/${cleanEndpoint}`;
  }

  /**
   * Logout user and clear tokens
   */
  private logout(): void {
    localStorage.clear();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
        errorMessage = error.error?.message || `Error: ${error.status} - ${error.statusText}`;
    }

    console.error('HTTP Error:', error);
    return throwError(() => new Error(errorMessage));
  }

  

  /**
   * GET request
   */
  GET<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.mergeHeaders(options?.headers, endpoint).pipe(
      switchMap((headers) => {
        return this.http.get<T>(this.getUrl(endpoint), {
          headers,
          params: options?.params,
          responseType: (options?.responseType as any) || 'json',
        }).pipe(
          catchError(this.handleError.bind(this))
        );
      })
    );
  }

  /**
   * POST request
   */
  POST<T>(endpoint: string, body: any, options?: RequestOptions): Observable<T> {
    return this.mergeHeaders(options?.headers, endpoint).pipe(
      switchMap((headers) => {
        return this.http.post<T>(this.getUrl(endpoint), body, {
          headers,
          params: options?.params,
          responseType: (options?.responseType as any) || 'json',
        }).pipe(
          catchError(this.handleError.bind(this))
        );
      })
    );
  }

  /**
   * PUT request
   */
  PUT<T>(endpoint: string, body: any, options?: RequestOptions): Observable<T> {
    return this.mergeHeaders(options?.headers, endpoint).pipe(
      switchMap((headers) => {
        return this.http.put<T>(this.getUrl(endpoint), body, {
          headers,
          params: options?.params,
          responseType: (options?.responseType as any) || 'json',
        }).pipe(
          catchError(this.handleError.bind(this))
        );
      })
    );
  }

  /**
   * PATCH request
   */
  PATCH<T>(endpoint: string, body: any, options?: RequestOptions): Observable<T> {
    return this.mergeHeaders(options?.headers, endpoint).pipe(
      switchMap((headers) => {
        return this.http.patch<T>(this.getUrl(endpoint), body, {
          headers,
          params: options?.params,
          responseType: (options?.responseType as any) || 'json',
        }).pipe(
          catchError(this.handleError.bind(this))
        );
      })
    );
  }

  /**
   * DELETE request
   */
  DELETE<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.mergeHeaders(options?.headers, endpoint).pipe(
      switchMap((headers) => {
        return this.http.delete<T>(this.getUrl(endpoint), {
          headers,
          params: options?.params,
          responseType: (options?.responseType as any) || 'json',
        }).pipe(
          catchError(this.handleError.bind(this))
        );
      })
    );
  }

  /**
   * Download file as blob
   */
  downloadFile(endpoint: string, params?: HttpParams): Observable<Blob> {
    return this.GET<Blob>(endpoint, {
      params,
      responseType: 'blob',
    });
  }
}